/*
 * Reusable table-row export. One function, five formats. Used by the
 * TableToolbar across every paginated/searchable table in the app so the
 * UX stays consistent: every list can be exported the same way.
 *
 * Format choices:
 *   - csv  → text/csv, fast path, no deps
 *   - json → application/json, fast path, no deps
 *   - xlsx → real Office Open XML via SheetJS (`xlsx`)
 *   - pdf  → real PDF via jsPDF + jspdf-autotable
 *   - docx → real Word DOCX via the `docx` package, table layout
 */

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  HeadingLevel
} from "docx";

export type ExportFormat = "csv" | "json" | "xlsx" | "pdf" | "docx";

export type ExportableRow = Record<string, unknown>;

export interface ExportOptions {
  filename: string;             // base name without extension; we append `.{ext}`
  columns: string[];            // ordered keys to include
  rows: ExportableRow[];
  title?: string;               // shown on PDF/DOCX as document heading
  columnLabels?: Record<string, string>; // pretty header names; falls back to key
}

function pickValue(row: ExportableRow, col: string): string {
  const v = row[col];
  if (v == null) return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvEscape(s: string): string {
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportCsv({ filename, columns, rows, columnLabels }: ExportOptions): void {
  const header = columns.map((c) => csvEscape(columnLabels?.[c] ?? c)).join(",");
  const body = rows.map((r) => columns.map((c) => csvEscape(pickValue(r, c))).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}\n`], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, `${filename}.csv`);
}

function exportJson({ filename, rows }: ExportOptions): void {
  const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
  downloadBlob(blob, `${filename}.json`);
}

function exportXlsx({ filename, columns, rows, title, columnLabels }: ExportOptions): void {
  const aoa: unknown[][] = [columns.map((c) => columnLabels?.[c] ?? c)];
  for (const r of rows) aoa.push(columns.map((c) => pickValue(r, c)));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (title ?? "Sheet 1").slice(0, 31));
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${filename}.xlsx`);
}

function exportPdf({ filename, columns, rows, title, columnLabels }: ExportOptions): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  if (title) {
    doc.setFontSize(14);
    doc.text(title, 40, 36);
  }
  autoTable(doc, {
    head: [columns.map((c) => columnLabels?.[c] ?? c)],
    body: rows.map((r) => columns.map((c) => pickValue(r, c))),
    startY: title ? 50 : 40,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [11, 37, 69], textColor: 255 } // brand navy
  });
  doc.save(`${filename}.pdf`);
}

async function exportDocx({ filename, columns, rows, title, columnLabels }: ExportOptions): Promise<void> {
  const headerRow = new TableRow({
    children: columns.map(
      (c) =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: columnLabels?.[c] ?? c, bold: true })] })]
        })
    )
  });
  const bodyRows = rows.map(
    (r) =>
      new TableRow({
        children: columns.map((c) => new TableCell({ children: [new Paragraph(pickValue(r, c))] }))
      })
  );
  const doc = new Document({
    sections: [
      {
        children: [
          ...(title ? [new Paragraph({ text: title, heading: HeadingLevel.HEADING_1 })] : []),
          new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [headerRow, ...bodyRows] })
        ]
      }
    ]
  });
  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `${filename}.docx`);
}

export async function exportRows(format: ExportFormat, options: ExportOptions): Promise<void> {
  switch (format) {
    case "csv":  return exportCsv(options);
    case "json": return exportJson(options);
    case "xlsx": return exportXlsx(options);
    case "pdf":  return exportPdf(options);
    case "docx": return exportDocx(options);
  }
}

export const EXPORT_FORMATS: Array<{ value: ExportFormat; label: string }> = [
  { value: "csv",  label: "CSV"  },
  { value: "xlsx", label: "Excel (xlsx)" },
  { value: "json", label: "JSON" },
  { value: "pdf",  label: "PDF"  },
  { value: "docx", label: "Word (docx)" }
];
